/** The site's language and time zone: choices for Site details, and conversions for scheduling a post in the site's own zone. */

/** The language tags offered first; whatever is stored but missing from here is added to the list. */
export const LANGUAGE_CHOICES: ReadonlyArray<{ tag: string; label: string }> = [
  { tag: "en-SG", label: "English (Singapore)" },
  { tag: "en-US", label: "English (United States)" },
  { tag: "en-GB", label: "English (United Kingdom)" },
  { tag: "en-AU", label: "English (Australia)" },
  { tag: "en-MY", label: "English (Malaysia)" },
  { tag: "en-IN", label: "English (India)" },
  { tag: "ms", label: "Malay" },
  { tag: "zh-Hans", label: "Chinese (Simplified)" },
  { tag: "zh-Hant", label: "Chinese (Traditional)" },
  { tag: "ta", label: "Tamil" },
  { tag: "id", label: "Indonesian" },
  { tag: "tl", label: "Filipino" },
  { tag: "th", label: "Thai" },
  { tag: "vi", label: "Vietnamese" },
  { tag: "ja", label: "Japanese" },
  { tag: "ko", label: "Korean" },
  { tag: "hi", label: "Hindi" },
  { tag: "ar", label: "Arabic" },
  { tag: "fr", label: "French" },
  { tag: "de", label: "German" },
  { tag: "es", label: "Spanish" },
  { tag: "pt", label: "Portuguese" },
];

/** The choices with the stored tag added when it isn't one of them. */
export function languageChoices(stored?: string | null): Array<{ tag: string; label: string }> {
  const list = [...LANGUAGE_CHOICES];
  if (stored && !list.some((choice) => choice.tag === stored)) list.unshift({ tag: stored, label: languageName(stored) });
  return list;
}

/** A readable name for a tag the browser knows ("Malay (Brunei)"), else the tag itself. */
export function languageName(tag: string): string {
  try { return new Intl.DisplayNames(["en"], { type: "language" }).of(tag) ?? tag; } catch { return tag; }
}

/** The browser's own language, as a tag ("en-SG"), or undefined when it can't say. */
export function browserLanguage(): string | undefined {
  try {
    const [tag] = Intl.getCanonicalLocales(typeof navigator === "undefined" ? [] : navigator.language ?? []);
    return tag;
  } catch { return undefined; }
}

/** The browser's time zone ("Asia/Singapore"), or undefined when it can't say. */
export function browserTimeZone(): string | undefined {
  try { return new Intl.DateTimeFormat().resolvedOptions().timeZone || undefined; } catch { return undefined; }
}

/** Every IANA zone the browser knows, with `first` (the one in use) at the top. */
export function timeZoneChoices(first?: string | null): string[] {
  let zones: string[] = [];
  try { zones = (Intl as unknown as { supportedValuesOf?: (key: string) => string[] }).supportedValuesOf?.("timeZone") ?? []; } catch { /* an older browser: only the zones named below */ }
  const all = new Set<string>(zones);
  all.add("UTC");
  const rest = [...all].filter((zone) => zone !== first).sort((a, b) => a.localeCompare(b));
  return first ? [first, ...rest] : rest;
}

/** "Singapore": the last part of the zone's name. */
export function zoneCity(zone: string): string {
  return (zone.split("/").pop() ?? zone).replace(/_/g, " ");
}

/** "GMT+8", "GMT+5:30" or "GMT" at an instant. */
export function zoneOffsetLabel(zone: string, at: number = Date.now()): string {
  try {
    return new Intl.DateTimeFormat("en", { timeZone: zone, timeZoneName: "shortOffset" }).formatToParts(at).find((part) => part.type === "timeZoneName")?.value ?? "";
  } catch { return ""; }
}

/** "Singapore time (GMT+8)", the label next to a time shown in `zone`. */
export function zoneLabel(zone: string, at: number = Date.now()): string {
  const offset = zoneOffsetLabel(zone, at);
  return zone === "UTC" ? "UTC" : `${zoneCity(zone)} time${offset ? ` (${offset})` : ""}`;
}

function wallParts(at: number, zone: string): { year: number; month: number; day: number; hour: number; minute: number } {
  const parts = new Intl.DateTimeFormat("en-US", { timeZone: zone, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }).formatToParts(at);
  const read = (type: string) => Number(parts.find((part) => part.type === type)?.value);
  return { year: read("year"), month: read("month"), day: read("day"), hour: read("hour") % 24, minute: read("minute") };
}

const pad = (value: number) => String(value).padStart(2, "0");

/** The zone's offset from UTC, in milliseconds, at an instant. */
function offsetAt(at: number, zone: string): number {
  const wall = wallParts(at, zone);
  return Date.UTC(wall.year, wall.month - 1, wall.day, wall.hour, wall.minute) - Math.floor(at / 60_000) * 60_000;
}

/** An instant as `YYYY-MM-DDTHH:mm` on the wall clock of `zone` (what a `datetime-local` input holds). Empty for a bad date. */
export function utcToZoned(iso: string | undefined, zone: string): string {
  const time = iso ? Date.parse(iso) : Number.NaN;
  if (Number.isNaN(time)) return "";
  try {
    const wall = wallParts(time, zone);
    return `${wall.year}-${pad(wall.month)}-${pad(wall.day)}T${pad(wall.hour)}:${pad(wall.minute)}`;
  } catch { return ""; }
}

/**
 * A `YYYY-MM-DDTHH:mm` wall-clock time in `zone` as a UTC ISO string, or null when it isn't a date.
 * A time that happens twice when clocks go back is read as the first one; a time skipped when
 * they go forward is read as the same wall time an hour later.
 */
export function zonedToUtc(local: string, zone: string): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(local);
  if (!match) return null;
  const [year, month, day, hour, minute] = match.slice(1).map(Number) as [number, number, number, number, number];
  const asUtc = Date.UTC(year, month - 1, day, hour, minute);
  if (Number.isNaN(asUtc)) return null;
  try {
    // The offsets a day either side are the ones before and after any clock change around the time.
    const before = offsetAt(asUtc - 86_400_000, zone);
    const after = offsetAt(asUtc + 86_400_000, zone);
    const fits = [...new Set([before, after])].map((offset) => asUtc - offset).filter((candidate) => offsetAt(candidate, zone) === asUtc - candidate);
    return new Date(fits.length ? Math.min(...fits) : asUtc - before).toISOString();
  } catch { return null; }
}

/** The default for "On a date": 9:00 tomorrow on the wall clock of `zone`. */
export function tomorrowMorning(zone: string, now: number = Date.now()): string {
  const [day] = utcToZoned(new Date(now + 86_400_000).toISOString(), zone).split("T");
  return zonedToUtc(`${day}T09:00`, zone) ?? new Date(now + 86_400_000).toISOString();
}

/** A date or date and time as the owner reads it, in `zone` (the browser's when none) and `language` (the browser's when none). */
export function formatInZone(iso: string | undefined, options: { zone?: string | undefined; language?: string | undefined; withTime?: boolean }): string {
  const time = iso ? Date.parse(iso) : Number.NaN;
  if (Number.isNaN(time)) return "";
  const shape: Intl.DateTimeFormatOptions = { day: "numeric", month: "short", year: "numeric", ...(options.withTime ? { hour: "numeric", minute: "2-digit" } : {}) };
  for (const locale of [options.language, undefined]) {
    for (const timeZone of [options.zone, undefined]) {
      try { return new Intl.DateTimeFormat(locale, { ...shape, ...(timeZone ? { timeZone } : {}) }).format(time); } catch { /* an unknown language or zone: try the browser's */ }
    }
  }
  return "";
}
